import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { CrudTable, type CrudColumn } from '@/shared/components/ui/crud-table';
import { useIsAdminTier } from '@/shared/auth/SessionProvider';
import { StatusBadge } from '@/shared/components/ui/status-badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Switch } from '@/shared/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  createPuntoVenta,
  FEX_DESVIO_MAX_PCT_DEFAULT,
  listAllPuntosVenta,
  softDeletePuntoVenta,
  updatePuntoVenta,
  type PuntoVentaItem,
  type PuntoVentaPayload,
} from '@/shared/api/controllers/puntosVenta';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import type { EntityViewProps } from '../../types';

const QUERY_KEY = ['configuracion', 'puntos-venta'] as const;

const CONDICIONES_IVA = [
  'Responsable Inscripto',
  'Monotributista',
  'Exento',
] as const;
type CondicionIva = (typeof CONDICIONES_IVA)[number];

type Mode =
  | { kind: 'closed' }
  | { kind: 'create' }
  | { kind: 'edit'; item: PuntoVentaItem }
  | { kind: 'delete'; item: PuntoVentaItem };

const TRANSMISIONES = ['', 'ADC', 'SCA'] as const;

const schema = z.object({
  razonSocial: z.string().trim().min(1, 'Ingresá la Razón Social'),
  nroPuntoVenta: z
    .string()
    .trim()
    .min(1, 'Ingresá el Nº de Punto de Venta')
    .refine((v) => /^\d+$/.test(v) && Number(v) > 0, 'Debe ser un número mayor a 0'),
  domicilio: z.string().trim().optional(),
  ingresosBrutos: z.string().trim().optional(),
  inicioActividades: z.string().trim().optional(),
  condicionIva: z.enum(CONDICIONES_IVA),
  // FCE MiPyME (opcional): CBU (22 dígitos) + transmisión (ADC/SCA).
  cbu: z
    .string()
    .trim()
    .optional()
    .refine((v) => !v || /^\d{22}$/.test(v), 'El CBU debe tener 22 dígitos'),
  fceTransmision: z.enum(TRANSMISIONES),
  // Punto de venta de exportación (WSFEX, Factura E).
  esExportacion: z.boolean(),
  // Factura E: diferencia máxima (%) entre la cotización declarada y la de ARCA.
  fexDesvioMaxPct: z
    .string()
    .trim()
    .refine((v) => {
      const n = Number(v.replace(',', '.'));
      return Number.isFinite(n) && n > 0 && n <= 100;
    }, 'Entre 0 y 100'),
  activo: z.boolean(),
});

type Values = z.infer<typeof schema>;

const empty: Values = {
  razonSocial: '',
  nroPuntoVenta: '',
  domicilio: '',
  ingresosBrutos: '',
  inicioActividades: '',
  condicionIva: 'Responsable Inscripto',
  cbu: '',
  fceTransmision: '',
  esExportacion: false,
  fexDesvioMaxPct: String(FEX_DESVIO_MAX_PCT_DEFAULT),
  activo: true,
};

function itemToForm(item: PuntoVentaItem): Values {
  const f = item.fields;
  const cond = (f.CondicionIva ?? '') as string;
  const transm = (f.FceTransmision ?? '').toUpperCase();
  return {
    razonSocial: f.RazonSocial ?? '',
    nroPuntoVenta: f.NroPuntoVenta != null ? String(f.NroPuntoVenta) : '',
    domicilio: f.Domicilio ?? '',
    ingresosBrutos: f.IngresosBrutos ?? '',
    inicioActividades: f.InicioActividades ?? '',
    condicionIva: CONDICIONES_IVA.includes(cond as CondicionIva)
      ? (cond as CondicionIva)
      : 'Responsable Inscripto',
    cbu: f.Cbu ?? '',
    fceTransmision: (transm === 'ADC' || transm === 'SCA' ? transm : '') as
      | ''
      | 'ADC'
      | 'SCA',
    esExportacion: f.EsExportacion === true,
    fexDesvioMaxPct: String(f.FexDesvioMaxPct ?? FEX_DESVIO_MAX_PCT_DEFAULT).replace('.', ','),
    activo: (f.Status ?? 'Activo') === 'Activo',
  };
}

function formToPayload(v: Values): PuntoVentaPayload {
  return {
    razonSocial: v.razonSocial.trim(),
    nroPuntoVenta: Number(v.nroPuntoVenta),
    domicilio: v.domicilio?.trim() || null,
    ingresosBrutos: v.ingresosBrutos?.trim() || null,
    inicioActividades: v.inicioActividades?.trim() || null,
    condicionIva: v.condicionIva,
    cbu: v.cbu?.trim() || null,
    fceTransmision: (v.fceTransmision || null) as 'ADC' | 'SCA' | null,
    esExportacion: v.esExportacion,
    fexDesvioMaxPct: Number(v.fexDesvioMaxPct.replace(',', '.')) || FEX_DESVIO_MAX_PCT_DEFAULT,
    status: v.activo ? 'Activo' : 'Inactivo',
  };
}

export function PuntosVentaEntity({
  search,
  filterValue,
  onStatus,
  openCreate,
  onCreateHandled,
}: EntityViewProps) {
  const queryClient = useQueryClient();
  const { config: activeCountry } = useActiveCountry();
  const scopedKey = useMemo(
    () => [...QUERY_KEY, activeCountry.code] as readonly unknown[],
    [activeCountry.code],
  );
  const { data: rows = [], isPending, isFetching, error, refetch } = useQuery({
    queryKey: scopedKey,
    queryFn: listAllPuntosVenta,
    staleTime: 60_000,
  });
  const [mode, setMode] = useState<Mode>({ kind: 'closed' });

  useEffect(() => {
    if (openCreate) {
      setMode({ kind: 'create' });
      onCreateHandled();
    }
  }, [openCreate, onCreateHandled]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let sorted = [...rows].sort((a, b) =>
      (a.fields.RazonSocial ?? '').localeCompare(b.fields.RazonSocial ?? '', 'es'),
    );
    if (filterValue) sorted = sorted.filter((r) => r.fields.Status === filterValue);
    if (!q) return sorted;
    return sorted.filter((r) => {
      const n = (r.fields.RazonSocial ?? '').toLowerCase();
      const pv = String(r.fields.NroPuntoVenta ?? '');
      return n.includes(q) || pv.includes(q);
    });
  }, [rows, search, filterValue]);

  const columns: CrudColumn<PuntoVentaItem>[] = [
    {
      header: 'Status',
      width: 'w-28',
      render: (r) => <StatusBadge status={r.fields.Status} />,
    },
    {
      header: 'Razón Social',
      render: (r) => (
        <span className="font-medium text-foreground">{r.fields.RazonSocial || '—'}</span>
      ),
    },
    {
      header: 'Nº Punto de Venta',
      render: (r) =>
        r.fields.NroPuntoVenta != null ? (
          r.fields.NroPuntoVenta
        ) : (
          <span className="text-muted-foreground">— (sin asignar)</span>
        ),
    },
    {
      header: 'Condición IVA',
      render: (r) => r.fields.CondicionIva || '—',
    },
    {
      header: 'Tipo',
      render: (r) => (r.fields.EsExportacion ? 'Exportación (Factura E)' : 'Factura A / B'),
    },
  ];

  const closeDialog = () => setMode({ kind: 'closed' });
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    // Also refresh the Facturar dialog's active-PV selector (different key).
    queryClient.invalidateQueries({ queryKey: ['puntos-venta', 'active'] });
  };

  const canEdit = useIsAdminTier();
  return (
    <>
      <CrudTable
        hideActions={!canEdit}
        rows={filtered}
        columns={columns}
        loading={isPending}
        error={error instanceof Error ? error : null}
        onRetry={() => refetch()}
        emptyMessage={
          search
            ? `No hay resultados para "${search}".`
            : 'Sin Puntos de Venta. Creá el PV de ARCA para emitir CAE.'
        }
        onEdit={(r) => setMode({ kind: 'edit', item: r })}
        onDelete={(r) => setMode({ kind: 'delete', item: r })}
        canDelete={(r) => r.fields.Status === 'Activo'}
        deleteDisabledTitle={() => 'El registro ya está inactivo'}
        isBackgroundFetching={isFetching}
      />

      {(mode.kind === 'create' || mode.kind === 'edit') && (
        <PuntoVentaDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.kind === 'edit' ? mode.item : null}
          onSuccess={(msg) => {
            invalidate();
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}

      {mode.kind === 'delete' && (
        <DeletePuntoVentaDialog
          open
          onOpenChange={(o) => !o && closeDialog()}
          item={mode.item}
          onSuccess={(msg) => {
            invalidate();
            closeDialog();
            onStatus({ kind: 'success', message: msg });
          }}
          onError={(msg) => {
            closeDialog();
            onStatus({ kind: 'error', message: msg });
          }}
        />
      )}
    </>
  );
}

function PuntoVentaDialog({
  open,
  onOpenChange,
  item,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: PuntoVentaItem | null;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const isEdit = Boolean(item);
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: item ? itemToForm(item) : empty,
  });

  useEffect(() => {
    if (open) form.reset(item ? itemToForm(item) : empty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const mutation = useMutation({
    mutationFn: async (v: Values) => {
      const payload = formToPayload(v);
      if (isEdit && item) return updatePuntoVenta(item.id, payload);
      return createPuntoVenta(payload);
    },
    onSuccess: () =>
      onSuccess(
        isEdit
          ? 'El Punto de Venta se actualizó correctamente.'
          : 'El Punto de Venta se creó correctamente.',
      ),
    onError: (err) =>
      onError(err instanceof Error ? err.message : 'No se pudo guardar el Punto de Venta.'),
  });

  const onSubmit = form.handleSubmit((v) => mutation.mutate(v));
  const fieldError = (name: keyof Values) =>
    form.formState.errors[name]?.message as string | undefined;

  const watchedCond = form.watch('condicionIva');
  const watchedTransmision = form.watch('fceTransmision');

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-lg"
        processing={mutation.isPending && (isEdit ? 'Guardando cambios…' : 'Creando Punto de Venta…')}
      >
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar Punto de Venta' : 'Nuevo Punto de Venta'}</DialogTitle>
          <DialogDescription>
            Datos del emisor ARCA. El Nº de Punto de Venta es el que asigna AFIP; los
            demás campos se imprimen en la representación impresa de la factura.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-1 flex-col overflow-hidden" noValidate>
          <DialogBody className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="razonSocial">
                  Razón Social <span className="text-primary">*</span>
                </Label>
                <Input
                  id="razonSocial"
                  variant="filled"
                  placeholder="FORNITALIA S.A."
                  autoFocus
                  {...form.register('razonSocial')}
                />
                {fieldError('razonSocial') && (
                  <p className="text-xs font-medium text-destructive">{fieldError('razonSocial')}</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="nroPuntoVenta">
                  Nº Punto de Venta <span className="text-primary">*</span>
                </Label>
                <Input
                  id="nroPuntoVenta"
                  variant="filled"
                  inputMode="numeric"
                  placeholder="Ej: 1"
                  {...form.register('nroPuntoVenta')}
                />
                {fieldError('nroPuntoVenta') && (
                  <p className="text-xs font-medium text-destructive">{fieldError('nroPuntoVenta')}</p>
                )}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="domicilio">Domicilio</Label>
              <Input
                id="domicilio"
                variant="filled"
                placeholder="Av. …, Ciudad, Provincia"
                {...form.register('domicilio')}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="ingresosBrutos">Ingresos Brutos</Label>
                <Input
                  id="ingresosBrutos"
                  variant="filled"
                  placeholder="Nº IIBB / Convenio Multilateral"
                  {...form.register('ingresosBrutos')}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inicioActividades">Inicio de Actividades</Label>
                <Input
                  id="inicioActividades"
                  variant="filled"
                  placeholder="DD/MM/AAAA"
                  {...form.register('inicioActividades')}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>
                Condición frente al IVA <span className="text-primary">*</span>
              </Label>
              <Select
                value={watchedCond}
                onValueChange={(v) =>
                  form.setValue('condicionIva', v as CondicionIva, {
                    shouldDirty: true,
                    shouldValidate: true,
                  })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar" />
                </SelectTrigger>
                <SelectContent>
                  {CONDICIONES_IVA.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Factura E: los PV de exportación (WSFEX) se dan de alta aparte en ARCA y numeran
                aparte; este flag evita que una A/B tome este PV y que una Factura E tome uno de WSFE. */}
            <label
              htmlFor="pv-exportacion"
              className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2 text-sm"
            >
              <span>
                <span className="block font-medium text-foreground">Punto de venta de exportación</span>
                <span className="block text-xs text-muted-foreground">
                  Para Factura E (clientes del exterior). Debe ser un PV de tipo "Exportación" dado de alta en ARCA.
                </span>
              </span>
              <Switch
                id="pv-exportacion"
                checked={form.watch('esExportacion')}
                onCheckedChange={(v) => form.setValue('esExportacion', v, { shouldDirty: true })}
              />
            </label>
            {form.watch('esExportacion') && (
              <div className="space-y-1.5">
                <Label htmlFor="pv-fex-desvio">Desvío máximo de cotización (%)</Label>
                <Input
                  id="pv-fex-desvio"
                  inputMode="decimal"
                  className="max-w-32"
                  {...form.register('fexDesvioMaxPct')}
                />
                {form.formState.errors.fexDesvioMaxPct ? (
                  <p className="text-xs text-destructive">{form.formState.errors.fexDesvioMaxPct.message}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Factura E: si la cotización ingresada difiere de la de ARCA más que esto, no se emite.
                  </p>
                )}
              </div>
            )}

            {/* FCE MiPyME (opcional) — solo se usa cuando ARCA exige emitir un
                comprobante de crédito electrónico (Factura de Crédito). */}
            <div className="space-y-3 rounded-lg border bg-muted/20 px-3 py-3">
              <p className="text-xs font-semibold text-muted-foreground">
                Factura de Crédito Electrónica (MiPyME) — opcional
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="cbu">CBU</Label>
                  <Input
                    id="cbu"
                    variant="filled"
                    inputMode="numeric"
                    placeholder="22 dígitos"
                    {...form.register('cbu')}
                  />
                  {fieldError('cbu') && (
                    <p className="text-xs font-medium text-destructive">{fieldError('cbu')}</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label>Transmisión</Label>
                  <Select
                    value={watchedTransmision === '' ? 'none' : watchedTransmision}
                    onValueChange={(v) =>
                      form.setValue('fceTransmision', (v === 'none' ? '' : v) as '' | 'ADC' | 'SCA', {
                        shouldDirty: true,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Ninguna</SelectItem>
                      <SelectItem value="ADC">ADC — Agente de Depósito Colectivo</SelectItem>
                      <SelectItem value="SCA">SCA — Sistema de Circulación Abierta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                El CBU debe estar registrado en ARCA para el régimen FCE. Se usa solo cuando
                corresponde emitir un comprobante MiPyME (ARCA lo exige por monto y condiciones).
              </p>
            </div>

            {isEdit && (
              <label
                htmlFor="pv-activo"
                className="flex items-center justify-between gap-3 rounded-lg border bg-muted/30 px-3 py-2 text-sm"
              >
                <span className="font-medium text-foreground">Activo</span>
                <Switch
                  id="pv-activo"
                  checked={form.watch('activo')}
                  onCheckedChange={(v) => form.setValue('activo', v, { shouldDirty: true })}
                />
              </label>
            )}
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {isEdit ? 'Guardar cambios' : 'Añadir'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DeletePuntoVentaDialog({
  open,
  onOpenChange,
  item,
  onSuccess,
  onError,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  item: PuntoVentaItem;
  onSuccess: (msg: string) => void;
  onError: (msg: string) => void;
}) {
  const mutation = useMutation({
    mutationFn: async () => softDeletePuntoVenta(item.id),
    onSuccess: () => onSuccess('Punto de Venta dado de baja.'),
    onError: (err) =>
      onError(err instanceof Error ? err.message : 'No se pudo dar de baja el Punto de Venta.'),
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent className="sm:max-w-md" processing={mutation.isPending && 'Dando de baja…'}>
        <DialogHeader>
          <DialogTitle>Dar de baja Punto de Venta</DialogTitle>
          <DialogDescription>
            ¿Confirmás dar de baja{' '}
            <strong className="text-foreground">{item.fields.RazonSocial || '—'}</strong> (PV{' '}
            {item.fields.NroPuntoVenta ?? '—'})? Las facturas ya emitidas no se ven afectadas.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={() => mutation.mutate()} disabled={mutation.isPending}>
            Dar de baja
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
